/**
 * App Router
 * ==========
 * All routes defined here. Pages are lazy-loaded so initial bundle stays small.
 *
 * Route structure:
 *   /                → HomePage (browse + discover)
 *   /add-book        → AddBookPage (4-step listing flow)
 *   /my-shelf        → MyShelfPage (user's books + borrows)
 *   /requests        → RequestsPage (borrow requests inbox)
 *   /auth/callback   → AuthCallbackPage (magic link handler)
 *   *                → NotFoundPage
 */

import React, { lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppLayout } from "@/components/layout/AppLayout";

// Lazy-loaded pages — code splits automatically
const HomePage         = lazy(() => import("@/pages/HomePage").then(m => ({ default: m.HomePage })));
const AddBookPage      = lazy(() => import("@/pages/AddBookPage").then(m => ({ default: m.AddBookPage })));
const MyShelfPage      = lazy(() => import("@/pages/MyShelfPage").then(m => ({ default: m.MyShelfPage })));
const RequestsPage     = lazy(() => import("@/pages/RequestsPage").then(m => ({ default: m.RequestsPage })));
const AuthCallbackPage = lazy(() => import("@/pages/AuthCallbackPage").then(m => ({ default: m.AuthCallbackPage })));
const NotFoundPage        = lazy(() => import("@/pages/NotFoundPage").then(m => ({ default: m.NotFoundPage })));
const PrivacyPolicyPage   = lazy(() => import("@/pages/PrivacyPolicyPage").then(m => ({ default: m.PrivacyPolicyPage })));
const ContactPage         = lazy(() => import("@/pages/ContactPage").then(m => ({ default: m.ContactPage })));

// React Query client — global config
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime:          2 * 60 * 1000, // 2 minutes
      gcTime:            10 * 60 * 1000, // 10 minutes
      retry:              1,
      refetchOnWindowFocus: false,       // prevents Firestore spam on tab switch
    },
    mutations: {
      retry: 0,
    },
  },
});

export const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <Routes>
        <Route element={<AppLayout />}>
          <Route index           element={<HomePage />}/>
          <Route path="add-book" element={<AddBookPage />}/>
          <Route path="my-shelf" element={<MyShelfPage />}/>
          <Route path="requests" element={<RequestsPage />}/>
          <Route path="auth/callback" element={<AuthCallbackPage />}/>
          <Route path="privacy"       element={<PrivacyPolicyPage />}/>
          <Route path="contact"       element={<ContactPage />}/>
          <Route path="*"             element={<NotFoundPage />}/>
        </Route>
      </Routes>
    </BrowserRouter>
  </QueryClientProvider>
);
